// @flow

import React from 'react';
import * as Yup from 'yup';
import * as Actions from 'main-app/store/Actions';
import { i18n } from 'shared/utils';
import AddReturnOrderMutation from 'main-app/mutations/AddReturnOrder';
import Divider from 'shared/components/common/Divider';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';
import styled from 'styled-components';
import {
  Formik,
  Form,
  FieldGroup,
  FieldGroupRow,
  SelectOrderField,
  SelectCustomerField,
  SelectItemField,
} from 'shared/components/form';

const ItemsPreview = styled.div`
  margin-top: 8px;
  margin-bottom: 8px;
`;

const ItemsPreviewTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 4px;
  font-size: 13px;
`;

const ItemName = styled.span`
  color: #333;
`;

const ItemQty = styled.span`
  color: #666;
  margin-left: 12px;
  white-space: nowrap;
`;

const RemoveBtn = styled.span`
  color: #ff4d4f;
  cursor: pointer;
  margin-left: 12px;
  font-size: 12px;
  &:hover {
    text-decoration: underline;
  }
`;

const RETURN_TYPE_OPTIONS = [
  { label: 'Refund', value: 'REFUND' },
  { label: 'Replacement', value: 'REPLACEMENT' },
  { label: 'Repair', value: 'REPAIR' },
  { label: 'Credit', value: 'CREDIT' },
];

const REASON_OPTIONS = [
  { label: 'Defective', value: 'DEFECTIVE' },
  { label: 'Wrong Item', value: 'WRONG_ITEM' },
  { label: 'Damaged', value: 'DAMAGED' },
  { label: 'Not as Described', value: 'NOT_AS_DESCRIBED' },
  { label: 'Customer Changed Mind', value: 'CUSTOMER_CHANGED_MIND' },
  { label: 'Other', value: 'OTHER' },
];

const CONDITION_OPTIONS = [
  { label: 'New', value: 'NEW' },
  { label: 'Good', value: 'GOOD' },
  { label: 'Damaged', value: 'DAMAGED' },
  { label: 'Defective', value: 'DEFECTIVE' },
  { label: 'Unsalvageable', value: 'UNSALVAGEABLE' },
];

const DISPOSITION_OPTIONS = [
  { label: 'Restock', value: 'RESTOCK' },
  { label: 'Scrap', value: 'SCRAP' },
  { label: 'Repair', value: 'REPAIR' },
  { label: 'Quarantine', value: 'QUARANTINE' },
];

type Props = {
  onClose: () => void,
  onSuccess?: (returnOrder: Object) => void,
};

const AddReturnOrderModal = ({ onClose, onSuccess }: Props) => {
  return (
    <Modal isOpen onClose={onClose}>
      <Formik
        initialValues={{
          orderId: '',
          customerId: '',
          returnType: '',
          reason: '',
          notes: '',
          customerNotes: '',
          lines: [],
          newItem: '',
          newItemQty: 1,
          newItemCondition: '',
          newItemDisposition: 'RESTOCK',
        }}
        validationSchema={Yup.object().shape({
          customerId: Yup.mixed().required(i18n.t('Customer is required')),
        })}
        onSubmit={async (values, { setSubmitting }) => {
          const customerIdVal =
            values.customerId && typeof values.customerId === 'object'
              ? values.customerId.value
              : values.customerId;
          const orderIdVal =
            values.orderId && typeof values.orderId === 'object'
              ? values.orderId.value
              : values.orderId;
          const returnTypeVal =
            values.returnType && typeof values.returnType === 'object'
              ? values.returnType.value
              : values.returnType;
          const reasonVal =
            values.reason && typeof values.reason === 'object'
              ? values.reason.value
              : values.reason;

          const input = {
            customerId: customerIdVal,
            orderId: orderIdVal || null,
            returnType: returnTypeVal || null,
            reason: reasonVal || null,
            notes: values.notes || null,
            customerNotes: values.customerNotes || null,
          };

          if (values.lines && values.lines.length > 0) {
            input.lines = values.lines.map(l => ({
              itemId: l.itemId,
              quantity: l.quantity,
              condition: l.condition || null,
              disposition: l.disposition || null,
              notes: null,
            }));
          }

          try {
            const response = await AddReturnOrderMutation.commit({
              variables: { input },
            });

            const newReturnOrder =
              response.addReturnOrder.returnOrderEdge.node;

            onClose();
            if (onSuccess) {
              onSuccess(newReturnOrder);
            }

            Actions.alertNotification(
              i18n.t('Return order created successfully'),
              'success',
            );
          } catch (e) {
            setSubmitting(false);
            Actions.alertNotification(e.message, 'error');
          }
        }}
        render={({
          errors,
          values,
          isValid,
          isSubmitting,
          handleSubmit,
          setFieldValue,
        }) => (
          <Form>
            <ModalHeader header={i18n.t('New Return Order')} onClose={onClose} />
            <ModalBody>
              <FieldGroupRow
                left={
                  <SelectCustomerField
                    name="customerId"
                    label={i18n.t('Customer')}
                    error={errors.customerId}
                  />
                }
                right={
                  <SelectOrderField
                    name="orderId"
                    label={i18n.t('Order (Optional)')}
                    error={errors.orderId}
                    queryVariables={
                      values.customerId && values.customerId.value
                        ? { customerIds: [values.customerId.value] }
                        : undefined
                    }
                    onChange={order => {
                      if (!order || !order.value) {
                        setFieldValue('lines', []);
                        return;
                      }
                      const data = order.data;
                      if (data && data.customer) {
                        setFieldValue('customerId', {
                          value: data.customer.id,
                          label: data.customer.name,
                          data: data.customer,
                        });
                      }
                      if (data && data.yields && data.yields.length > 0) {
                        const orderLines = data.yields.map(y => ({
                          itemId: y.item.id,
                          itemName: y.item.name,
                          quantity: y.orderedQuantity || 1,
                          uomSymbol: y.item.quantityUOM
                            ? y.item.quantityUOM.symbol
                            : '',
                          condition: null,
                          disposition: 'RESTOCK',
                        }));
                        setFieldValue('lines', orderLines);
                      }
                    }}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="returnType"
                    label={i18n.t('Return Type')}
                    type="select"
                    options={RETURN_TYPE_OPTIONS}
                  />
                }
                right={
                  <FieldGroup
                    name="reason"
                    label={i18n.t('Reason')}
                    type="select"
                    options={REASON_OPTIONS}
                  />
                }
              />
              <FieldGroup
                name="notes"
                label={i18n.t('Internal Notes')}
                type="textarea"
              />
              <FieldGroup
                name="customerNotes"
                label={i18n.t('Customer Notes')}
                type="textarea"
              />

              <Divider />

              <ItemsPreviewTitle>{i18n.t('Return Lines')}</ItemsPreviewTitle>

              {values.lines && values.lines.length > 0 && (
                <ItemsPreview>
                  {values.lines.map((line, idx) => (
                    <ItemRow key={idx}>
                      <ItemName>{line.itemName}</ItemName>
                      <div>
                        <ItemQty>
                          {i18n.t('Qty')}: {line.quantity}
                          {line.uomSymbol ? ` ${line.uomSymbol}` : ''}
                        </ItemQty>
                        {line.condition && (
                          <ItemQty>{line.condition.replace(/_/g, ' ')}</ItemQty>
                        )}
                        {line.disposition && (
                          <ItemQty>{line.disposition.replace(/_/g, ' ')}</ItemQty>
                        )}
                        <RemoveBtn
                          onClick={() => {
                            const updated = [...values.lines];
                            updated.splice(idx, 1);
                            setFieldValue('lines', updated);
                          }}
                        >
                          {i18n.t('Remove')}
                        </RemoveBtn>
                      </div>
                    </ItemRow>
                  ))}
                </ItemsPreview>
              )}

              <FieldGroupRow
                left={
                  <SelectItemField
                    name="newItem"
                    label={i18n.t('Add Item')}
                    excludeIds={values.lines.map(l => l.itemId)}
                  />
                }
                center={
                  <FieldGroup
                    name="newItemQty"
                    label={i18n.t('Qty')}
                    type="number"
                  />
                }
                right={
                  <FieldGroup
                    name="newItemCondition"
                    label={i18n.t('Condition')}
                    type="select"
                    options={CONDITION_OPTIONS}
                  />
                }
              />
              <FieldGroupRow
                left={
                  <FieldGroup
                    name="newItemDisposition"
                    label={i18n.t('Disposition')}
                    type="select"
                    options={DISPOSITION_OPTIONS}
                  />
                }
              />
              <Button
                theme="border-blue"
                disabled={!values.newItem}
                onClick={() => {
                  const item = values.newItem;
                  const itemId =
                    item && typeof item === 'object' ? item.value : item;
                  const itemName =
                    item && typeof item === 'object'
                      ? item.label
                      : item;
                  const uomSymbol =
                    item &&
                    typeof item === 'object' &&
                    item.data &&
                    item.data.quantityUOM
                      ? item.data.quantityUOM.symbol
                      : '';
                  const conditionVal =
                    values.newItemCondition &&
                    typeof values.newItemCondition === 'object'
                      ? values.newItemCondition.value
                      : values.newItemCondition;
                  const dispositionVal =
                    values.newItemDisposition &&
                    typeof values.newItemDisposition === 'object'
                      ? values.newItemDisposition.value
                      : values.newItemDisposition;

                  setFieldValue('lines', [
                    ...values.lines,
                    {
                      itemId,
                      itemName: itemName || itemId,
                      quantity: values.newItemQty || 1,
                      uomSymbol,
                      condition: conditionVal || null,
                      disposition: dispositionVal || 'RESTOCK',
                    },
                  ]);
                  setFieldValue('newItem', '');
                  setFieldValue('newItemQty', 1);
                  setFieldValue('newItemCondition', '');
                  setFieldValue('newItemDisposition', 'RESTOCK');
                }}
              >
                {i18n.t('Add Return Line')}
              </Button>
            </ModalBody>
            <ModalFooter>
              <Button theme="border-white" onClick={onClose}>
                {i18n.t('Cancel')}
              </Button>
              <Button
                theme="blue"
                loading={isSubmitting}
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit}
              >
                {i18n.t('Create Return')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      />
    </Modal>
  );
};

export default AddReturnOrderModal;
